import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DataGridPro as MuiGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridExpandMoreIcon,
  gridFilteredSortedRowIdsSelector,
  gridExpandedSortedRowIdsSelector,
  GridOverlay,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {
  Portal,
  Snackbar,
  IconButton,
  LinearProgress,
  Box,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import { merge } from 'lodash-es';
import { useFields } from '../../hooks/use-fields';
import { useGrowers } from '../../hooks/use-growers';
import { selectors as fieldSelectors } from '../../store/dux/fields-dux';
import { usePermissions } from '../../hooks/use-permissions';
import ActionButton from '../ActionButton';
import { usePermission } from '../../hooks/use-permission';
import { useFeatureFlag } from '../../hooks/use-feature-flag';

export { GridActionsCellItem } from '@mui/x-data-grid-pro';
export { Pagination } from './Pagination.jsx';

const style = {
  '& .MuiDataGrid-row.Mui-selected': {
    background: '#F5F8F4',
  },
  '& .MuiDataGrid-row.focused': {
    backgroundColor: '#e7ede4',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    overflow: 'visible',
    whiteSpace: 'break-spaces',
    lineHeight: 1,
    textAlign: 'left',
  },
  '@media (pointer: coarse)': {
    '& .MuiDataGrid-menuIcon': {
      display: 'none',
    },
  },
  '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
    {
      pl: 1,
    },
};

export function Table(props) {
  const {
    actionsRef,
    tabs,
    preActions,
    actions,
    fields,
    rows,
    controlColumns,
    columns,
    sx,
    onFiltered,
    isRowSelectable,
    getCellWarning,
    getCellError,
    allowContractSelection,
    hideContractErrorIcon,
    isLpkContractRestricted,
    hiddenColumns,
    initialState,
    ...passThroughProps
  } = props;

  const { selectedAaids, selectFields, focusAaid, focusedAaid } = useFields();
  const { selected: selectedGrower } = useGrowers();
  const apiRef = useGridApiRef();
  const isAdmin = usePermissions().includes('DOWNLOAD_UNCONTRACTED_RX');
  const showActions = usePermission('VIEW_ACTIONS');
  const canDownload = usePermission('DOWNLOAD_RX');
  const [totalVisibleRows, setTotalVisibleRows] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [filteredRows, setFilteredRows] = useState(null);
  const removeFieldPermissions = useFeatureFlag('RemoveFieldPermissions');
  const growerFields = useSelector(fieldSelectors.growerFields);
  const focusedRow = useMemo(
    () => props.focusedRow || focusedAaid,
    [focusedAaid, props.focusedRow],
  );

  const isContracted = useCallback(
    (row) => {
      if (selectedGrower?.organization?.isDefault === false) return true;

      if (!row.fields) {
        return row.isContractedFullAccess || row.isContractedLpkOnly;
      }

      return (
        row.fields.length ===
        row.isContractedFullAccessCount + row.isContractedLpkOnlyCount
      );
    },
    [selectedGrower],
  );

  const getContractError = useCallback(
    ({ row }) => {
      if (removeFieldPermissions) return null;

      const shouldShowContractError = hideContractErrorIcon
        ? false
        : !isContracted(row);

      const shouldShowLpkContractError = isLpkContractRestricted
        ? row.isContractedLpkOnly
        : false;

      if (shouldShowLpkContractError) {
        return 'Insufficient Contract';
      }

      if (shouldShowContractError) {
        return 'No Contract';
      }

      return null;
    },
    [
      hideContractErrorIcon,
      isContracted,
      isLpkContractRestricted,
      removeFieldPermissions,
    ],
  );

  const fieldColumns = useMemo(
    () =>
      withFieldColumns(
        columns,
        getCellWarning,
        [getContractError, getCellError],
        controlColumns,
      ),
    [columns, controlColumns, getCellError, getCellWarning, getContractError],
  );

  const totalFilteredRows = useMemo(
    () => (totalRows === totalVisibleRows ? 0 : totalVisibleRows),
    [totalRows, totalVisibleRows],
  );

  const isContractClickable = useMemo(
    () => isAdmin || allowContractSelection,
    [allowContractSelection, isAdmin],
  );

  const getRowClassName = useCallback(
    (params) => {
      const { isContractedLpkOnly } = params.row;
      const classes = [];
      if (removeFieldPermissions) {
        return '';
      }

      if (focusedRow === params.id) {
        classes.push('focused');
      }

      if (!isContractClickable) {
        if (isLpkContractRestricted && isContractedLpkOnly) {
          classes.push('error');
        }

        if (!isContracted(params.row)) {
          classes.push('error');
        }
      }

      return classes.join(' ');
    },
    [
      focusedRow,
      isContractClickable,
      isContracted,
      isLpkContractRestricted,
      removeFieldPermissions,
    ],
  );

  const getRowSelectable = useCallback(
    (params) => {
      const { isContractedFullAccess, isContractedLpkOnly } = params.row;

      if (removeFieldPermissions && canDownload) {
        return true;
      }

      if (isRowSelectable) {
        return isRowSelectable(params);
      }

      if (isContractClickable) {
        return true;
      }

      if (!isContracted(params.row)) {
        return false;
      }

      if (isLpkContractRestricted) {
        return isContractedFullAccess || !isContractedLpkOnly;
      }

      return true;
    },
    [
      isContractClickable,
      isRowSelectable,
      isContracted,
      isLpkContractRestricted,
      removeFieldPermissions,
      canDownload,
    ],
  );

  const onRowClick = useCallback(
    (row) => {
      const { id: aaid } = row;
      return focusedAaid === aaid ? focusAaid(null) : focusAaid(aaid);
    },
    [focusAaid, focusedAaid],
  );

  const onRowSelectionModelChange = useCallback(
    (ids) => {
      if (ids.includes(focusedAaid)) focusAaid(null);
      if (fields?.length) selectFields(ids);
    },
    [fields, selectFields, focusAaid, focusedAaid],
  );

  useEffect(() => {
    apiRef?.current?.subscribeEvent?.('stateChange', (params) => {
      setTotalRows(params.rows.totalRowCount);
      setTotalVisibleRows(gridFilteredSortedRowIdsSelector(apiRef).length);
    });
  }, [apiRef]);

  useEffect(() => {
    if (!totalFilteredRows) {
      setFilteredRows([]);
    } else {
      setFilteredRows(gridFilteredSortedRowIdsSelector(apiRef));
    }
  }, [apiRef, totalFilteredRows]);

  useEffect(() => {
    if (!apiRef?.current?.getSortedRowIds) return;

    const rowIds = gridExpandedSortedRowIdsSelector(apiRef);
    const rowIndex = rowIds.findIndex((id) => id === focusedRow);
    if (rowIndex < 0) return;

    try {
      apiRef.current.scrollToIndexes({ rowIndex });
    } catch (e) {
      // ignore, grid is not fully ready
    }
  }, [apiRef, focusedRow]);

  useEffect(() => {
    if (onFiltered && filteredRows) onFiltered(filteredRows);
  }, [filteredRows, onFiltered]);

  const fieldProps = fields && {
    checkboxSelection: true,
    disableRowSelectionOnClick: true,
    columns: fieldColumns,
    getRowId: (row) => row.aaManagedId || row.aaid,
    onRowClick,
    getRowClassName,
    rowSelectionModel: selectedAaids,
    onRowSelectionModelChange,
    hideFooter: false,
    localeText: {
      noRowsLabel: 'No fields',
      footerTotalRows: 'Total:',
      footerRowSelected: (count) => `${count.toLocaleString()} selected`,
    },
    initialState: merge(
      {
        pinnedColumns: {
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
          right: ['actions'],
        },
        sorting: {
          sortModel: [{ field: 'fieldName', sort: 'asc' }],
        },
        columns: {
          columnVisibilityModel: Object.fromEntries(
            (hiddenColumns || []).map((column) => [column, false]),
          ),
        },
      },
      initialState,
    ),
  };

  if ((!fields && !rows) || (fields && rows)) {
    throw new Error(
      `You must provide one of either fields or rows. If the table content are fields, use fields otherwise use rows.`,
    );
  }

  const rowsWithContractDetails = useMemo(
    () =>
      (fields || rows).map((row) => {
        const fieldsForRow = row.fields || [row];
        const contracts = fieldsForRow.map(
          mapContract.bind(null, growerFields),
        );
        const fullAccess = contracts.filter((c) => c.isContractedFullAccess);
        const lpkOnly = contracts.filter((c) => c.isContractedLpkOnly);
        const v2 = contracts.filter((c) => c.isContractedV2);

        return removeFieldPermissions
          ? { ...row }
          : {
              ...row,
              isContractedFullAccess: fullAccess.length === fieldsForRow.length,
              isContractedFullAccessCount: fullAccess.length,
              isContractedLpkOnly: lpkOnly.length === fieldsForRow.length,
              isContractedLpkOnlyCount: lpkOnly.length,
              isContractedV2: v2.length === fieldsForRow.length,
              isContractedV2Count: v2.length,
            };
      }),
    [fields, growerFields, rows, removeFieldPermissions],
  );

  const columnsWithMinWidth = useMemo(
    () =>
      columns.map((col) =>
        col.minWidth
          ? col
          : { ...col, minWidth: Math.round((col.flex || 1) * 100) },
      ),
    [columns],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: 0,
        height: '100%',
        flexDirection: 'column',
      }}
    >
      {buildToolBar()}
      <MuiGrid
        sx={{
          ...sx,
          ...style,
          flexGrow: 1,
          minHeight: 0,
          height: '100%',
          '& .MuiDataGrid-row.error': { color: '#aaa' },
        }}
        apiRef={apiRef}
        columnBuffer={20}
        rowBuffer={10}
        slots={{ loadingOverlay: LinearLoadingOverlay }}
        rows={rowsWithContractDetails}
        columns={columnsWithMinWidth}
        hideFooter
        initialState={initialState}
        {...fieldProps}
        {...passThroughProps}
        isRowSelectable={(params) => getRowSelectable(params)}
      />
    </Box>
  );

  function buildToolBar() {
    if (!actions && !preActions) return null;

    return (
      <Box sx={{ p: 1, pt: 1.5, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, mb: -1 }}>{tabs}</Box>
        <Box sx={{ display: 'flex', gap: 2 }}>{preActions}</Box>
        <Box>
          {showActions && actions && (
            <ActionButton apiRef={actionsRef}>{actions}</ActionButton>
          )}
        </Box>
      </Box>
    );
  }
}

function withFieldColumns(
  columns,
  getCellWarning,
  getCellErrors,
  controlColumns = [],
) {
  return [
    ...controlColumns.map(mapColumnDefinition),
    {
      field: 'fieldName',
      headerName: 'Field Name',
      flex: 1.5,
      minWidth: 100,
      renderCell: (params) => (
        <TableCell
          copyOnClick={params.row.aaManagedId}
          renderCellParams={params}
          getCellWarning={getCellWarning}
          getCellErrors={getCellErrors}
        />
      ),
    },
    {
      field: 'farmName',
      headerName: 'Farm Name',
      flex: 1,
    },
    {
      field: 'fieldId',
      headerName: 'Field ID',
      type: 'number',
      flex: 0.75,
      minWidth: 75,
      valueFormatter: ({ value }) => value && `${value}`,
    },
    {
      field: 'acres',
      headerName: 'Acres',
      type: 'number',
      flex: 0.75,
      minWidth: 75,
      valueFormatter: ({ value }) =>
        Number(value)
          ? Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })
          : '',
    },
    ...columns.map(mapColumnDefinition),
  ];
}

function mapColumnDefinition(column) {
  if (column.type === 'actions') return column;

  const valueFormatter =
    column.type === 'date' &&
    (({ value }) => value && new Date(value).toLocaleDateString());

  const renderCell =
    column.renderCell &&
    ((params) => (
      <TableCell renderCellParams={params}>
        {column.renderCell(params)}
      </TableCell>
    ));

  return { valueFormatter, ...column, renderCell };
}

export function TableCell(props) {
  const [showMessage, setShowMessage] = useState(false);
  const {
    renderCellParams,
    copyOnClick,
    copyTarget = 'AAID',
    getCellWarning,
    getCellErrors,
  } = props;
  const warning = getCellWarning && getCellWarning(renderCellParams);
  const error =
    getCellErrors &&
    getCellErrors
      .map((getCellError) => getCellError && getCellError(renderCellParams))
      .filter(Boolean)
      .join(', ');

  const renderToolTip = (message, color) => (
    <Tooltip title={message} disableFocusListener>
      <InfoIcon sx={{ ml: 0.5 }} fontSize="small" color={color} />
    </Tooltip>
  );

  return (
    <Box className="MuiDataGrid-cellContent" sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex' }}>
        <Box
          title={renderCellParams?.formattedValue?.toString()}
          sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {buildCellValue()}
        </Box>
        {error && renderToolTip(error, 'error')}
        {warning && renderToolTip(warning, 'warning')}
      </Box>
    </Box>
  );

  function buildCellValue() {
    const value = props.children || renderCellParams?.value;
    if (!copyOnClick) return value;

    return (
      <>
        {showMessage && (
          <Portal>
            <Snackbar
              open
              onClose={() => setShowMessage(false)}
              message={`${copyTarget} copied`}
              autoHideDuration={1000}
              severity="info"
            />
          </Portal>
        )}
        <Box
          sx={{ width: '100%', cursor: 'copy' }}
          onClick={(e) => e.stopPropagation()}
        >
          <CopyToClipboard
            text={copyOnClick}
            onCopy={() => setShowMessage(true)}
          >
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {value}
            </Box>
          </CopyToClipboard>
        </Box>
      </>
    );
  }
}

export function LinearLoadingOverlay() {
  return (
    <GridOverlay sx={{ position: 'relative', bgcolor: 'transparent' }}>
      <LinearProgress sx={{ position: 'absolute', bottom: 0, width: '100%' }} />
    </GridOverlay>
  );
}

export function TableDetailToggle(props) {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();

  const contentCache = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowsContentCacheSelector,
  );

  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <IconButton
      size="small"
      tabIndex={-1}
      disabled={!hasDetail}
      aria-label={isExpanded ? 'Close' : 'Open'}
    >
      <GridExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
}

function mapContract(growerFields, field) {
  const growerField = growerFields.find(
    (f) => f.aaManagedId === field.aaManagedId || f.aaManagedId === field.aaid,
  );

  return {
    isContractedFullAccess: growerField?.isContractedFullAccess,
    isContractedLpkOnly: growerField?.isContractedLpkOnly,
  };
}
